// @feature-spec brain/vidmax/feature-specs/admin-ad-engine.md
/**
 * `/admin/ad-engine` — the Ad Engine fleet index.
 *
 * Flat route file (`ad-engine.index.tsx`, not `ad-engine.tsx`) on purpose: a `ad-engine.tsx`
 * would become the layout parent of `ad-engine.$videoId.tsx` and mask the detail page unless it
 * rendered an `<Outlet/>` (see `routes/admin/CLAUDE.md` Common Mistakes).
 *
 * This file owns everything impure — the `adEngine.list` query, the team/search filters and the
 * navigation. `AdProjectList` stays a controlled, testable view.
 */
import { EAdReviewFilter, EAdStage } from "@aix/vidmax-shared/src/schemas/adProject"
import { AixContentHeader, Badge, Button, Combobox, Input } from "@aix/ui"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

import { AdProjectList } from "~/components/AdEngine/AdProjectList"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"
import { api } from "~/utils/api"

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300
const ALL_TEAMS = "all"

function AdEngineIndexPage() {
  const { team, q, format } = Route.useSearch()
  const routeNavigate = Route.useNavigate()
  const navigate = useNavigate()

  // `?q=` is the shareable value; the input is typed against local state so every keystroke is
  // not a router write (and not a query) — the URL catches up after the debounce.
  const [searchDraft, setSearchDraft] = useState(q ?? "")
  useEffect(() => {
    const timer = setTimeout(() => {
      void routeNavigate({ search: (prev) => ({ ...prev, q: searchDraft || undefined }), replace: true })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchDraft, routeNavigate])

  const listQuery = api.adEngine.list.useQuery({
    ...(team ? { teamId: team } : {}),
    ...(q ? { search: q } : {}),
    ...(format ? { formatId: format } : {}),
    limit: PAGE_SIZE,
  })

  const projects = useMemo(() => listQuery.data?.projects ?? [], [listQuery.data])
  // Same query key as the review page's default view, so opening it is a cache hit.
  const reviewCount = api.adEngine.reviewQueue.useQuery({ stage: EAdStage.KEYFRAME, filter: EAdReviewFilter.OPEN }).data
    ?.total

  // Team options come off the loaded rows rather than a second query, accumulated across loads
  // so that filtering to one team does not shrink the picker to just that team.
  const [teamsSeen, setTeamsSeen] = useState<Map<string, string>>(() => new Map())
  useEffect(() => {
    setTeamsSeen((prev) => {
      const next = new Map(prev)
      for (const project of projects) {
        if (project.teamId) next.set(project.teamId, project.teamName ?? project.teamId)
      }
      return next.size === prev.size ? prev : next
    })
  }, [projects])
  const teamOptions = useMemo(
    () => [
      { value: ALL_TEAMS, label: "All teams" },
      ...[...teamsSeen.entries()].map(([value, label]) => ({ value, label })),
    ],
    [teamsSeen],
  )

  return (
    <SuperAdminLayout>
      <CustomHead title="Ad Engine" />
      <div className="container mx-auto space-y-6 pb-12">
        <AixContentHeader className="px-8 pt-8">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold">Ad Engine</h1>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={reviewCount ? "default" : "outline"} asChild>
                <Link to="/admin/ad-engine/review">Review queue{reviewCount ? ` (${String(reviewCount)})` : ""} →</Link>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/admin/ad-engine/formats">Formats →</Link>
              </Button>
            </div>
          </div>
        </AixContentHeader>
        <div className="space-y-4 px-8">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Search titles…"
              className="max-w-xs"
            />
            <Combobox
              value={team ?? ALL_TEAMS}
              options={teamOptions}
              onValueChange={(value) =>
                void routeNavigate({
                  search: (prev) => ({ ...prev, team: value === ALL_TEAMS ? undefined : value }),
                  replace: true,
                })
              }
              label="Team"
              shortcutKeys={["shift", "t"]}
            />
            {format && (
              <Badge variant="secondary" className="gap-2">
                <span className="font-mono text-[11px]">{format}</span>
                <button
                  type="button"
                  aria-label="Clear the format filter"
                  onClick={() =>
                    void routeNavigate({ search: (prev) => ({ ...prev, format: undefined }), replace: true })
                  }
                >
                  ×
                </button>
              </Badge>
            )}
          </div>
          <AdProjectList
            projects={projects}
            total={listQuery.data?.total ?? 0}
            isLoading={listQuery.isLoading}
            hasFilters={Boolean(team || q || format)}
            onOpen={(videoId) => void navigate({ to: "/admin/ad-engine/$videoId", params: { videoId } })}
            onOpenFormat={(formatId) => void navigate({ to: "/admin/ad-engine/formats", search: { focus: formatId } })}
          />
        </div>
      </div>
    </SuperAdminLayout>
  )
}

interface IAdEngineIndexSearch {
  team?: string
  q?: string
  /** Exact `doc.formatId` — set by the catalogue's "N projects" button. */
  format?: string
}

export const Route = createFileRoute("/admin/ad-engine/")({
  component: AdEngineIndexPage,
  validateSearch: (search: Record<string, unknown>): IAdEngineIndexSearch => ({
    ...(typeof search.team === "string" && search.team ? { team: search.team } : {}),
    ...(typeof search.q === "string" && search.q ? { q: search.q } : {}),
    ...(typeof search.format === "string" && search.format ? { format: search.format } : {}),
  }),
})
